import React from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';

// Mobile-friendly card with large tap target
export const MobileCard = ({ 
  children, 
  className, 
  onClick, 
  isExpandable = false,
  isExpanded = false,
  ...props 
}) => {
  return (
    <div
      className={cn(
        "bg-card rounded-xl border border-border/50 shadow-sm transition-all",
        "active:scale-[0.99] touch-manipulation",
        onClick && "cursor-pointer hover:border-primary/30",
        className
      )}
      onClick={onClick}
      {...props}
    >
      {children}
      {isExpandable && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      )}
    </div>
  );
};

// Mobile-friendly list item with arrow
export const MobileListItem = ({ 
  icon: Icon, 
  title, 
  subtitle, 
  badge,
  onClick,
  className,
  rightContent,
  showArrow = true
}) => {
  return (
    <div
      className={cn(
        "flex items-center gap-3 p-4 bg-card rounded-xl border border-border/50",
        "active:scale-[0.99] touch-manipulation transition-all",
        onClick && "cursor-pointer hover:bg-muted/50",
        className
      )}
      onClick={onClick}
    >
      {Icon && (
        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate">{title}</p>
          {badge}
        </div>
        {subtitle && (
          <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
        )}
      </div>
      {rightContent}
      {showArrow && onClick && (
        <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
      )}
    </div>
  );
};

// Floating action button for mobile
export const FloatingActionButton = ({ 
  icon: Icon, 
  onClick, 
  label,
  className,
  position = 'bottom-right' // bottom-right, bottom-center, bottom-left
}) => {
  const positionClasses = {
    'bottom-right': 'right-4 bottom-20 md:bottom-6',
    'bottom-center': 'left-1/2 -translate-x-1/2 bottom-20 md:bottom-6',
    'bottom-left': 'left-4 bottom-20 md:bottom-6'
  };

  return (
    <Button
      onClick={onClick}
      className={cn(
        "fixed z-40 h-14 rounded-full shadow-lg",
        "touch-manipulation active:scale-95 transition-transform",
        positionClasses[position],
        label ? "px-6 gap-2" : "w-14",
        className
      )}
    >
      <Icon className="h-5 w-5" />
      {label && <span className="font-medium">{label}</span>}
    </Button>
  );
};

// Sticky bottom action bar for mobile forms
export const StickyBottomBar = ({ children, className }) => {
  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 z-40 p-4 bg-background/95 backdrop-blur-lg border-t border-border",
      "safe-area-bottom md:relative md:p-0 md:bg-transparent md:border-0 md:backdrop-blur-none",
      className
    )}>
      {children}
    </div>
  );
};

// Mobile-friendly section header
export const MobileSectionHeader = ({ 
  title, 
  subtitle, 
  action,
  className 
}) => {
  return (
    <div className={cn("flex items-center justify-between mb-3", className)}>
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        {subtitle && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  );
};

// Mobile-friendly empty state
export const MobileEmptyState = ({ 
  icon: Icon, 
  title, 
  description, 
  action,
  className 
}) => {
  return (
    <div className={cn(
      "flex flex-col items-center justify-center py-12 px-4 text-center",
      className
    )}>
      {Icon && (
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Icon className="h-8 w-8 text-muted-foreground" />
        </div>
      )}
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      {description && (
        <p className="text-muted-foreground text-sm mb-4 max-w-xs">{description}</p>
      )}
      {action}
    </div>
  );
};

// Pull-to-refresh indicator
export const PullToRefreshIndicator = ({ isRefreshing }) => {
  if (!isRefreshing) return null;
  
  return (
    <div className="flex justify-center py-4">
      <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );
};

// Quick action button for mobile (larger touch target)
export const QuickActionButton = ({ 
  icon: Icon, 
  label, 
  onClick, 
  variant = 'default',
  className,
  badge
}) => {
  const variants = {
    default: 'bg-primary/10 text-primary hover:bg-primary/20',
    success: 'bg-green-500/10 text-green-600 hover:bg-green-500/20',
    warning: 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20',
    danger: 'bg-red-500/10 text-red-600 hover:bg-red-500/20',
    muted: 'bg-muted text-muted-foreground hover:bg-muted/80'
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-center justify-center p-4 rounded-xl min-h-[88px] min-w-[88px]",
        "touch-manipulation active:scale-95 transition-all relative",
        variants[variant],
        className
      )}
    >
      <Icon className="h-6 w-6 mb-2" />
      <span className="text-xs font-medium text-center leading-tight">{label}</span>
      {badge && (
        <span className="absolute top-2 right-2 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  );
};

// Swipeable card actions (placeholder - needs gesture library)
export const SwipeableCard = ({ children, onSwipeLeft, onSwipeRight, className }) => {
  // Basic implementation - for full swipe support, consider react-swipeable
  return (
    <div className={cn("relative overflow-hidden", className)}>
      {children}
    </div>
  );
};

// Mobile-optimized date picker trigger
export const MobileDatePicker = ({ value, onChange, label, className }) => {
  return (
    <div className={cn("relative", className)}>
      {label && (
        <label className="block text-sm font-medium mb-1.5">{label}</label>
      )}
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full h-12 px-4 rounded-xl border border-border bg-background",
          "text-base appearance-none touch-manipulation",
          "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        )}
      />
    </div>
  );
};

// Mobile-optimized select
export const MobileSelect = ({ value, onChange, options, label, placeholder, className }) => {
  return (
    <div className={cn("relative", className)}>
      {label && (
        <label className="block text-sm font-medium mb-1.5">{label}</label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full h-12 px-4 rounded-xl border border-border bg-background",
          "text-base appearance-none touch-manipulation",
          "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        )}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none mt-3" />
    </div>
  );
};

// Mobile-optimized text input
export const MobileInput = ({ value, onChange, label, placeholder, type = 'text', className, ...props }) => {
  return (
    <div className={cn("relative", className)}>
      {label && (
        <label className="block text-sm font-medium mb-1.5">{label}</label>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "w-full h-12 px-4 rounded-xl border border-border bg-background",
          "text-base touch-manipulation",
          "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary",
          "placeholder:text-muted-foreground"
        )}
        {...props}
      />
    </div>
  );
};

// Mobile-optimized textarea
export const MobileTextarea = ({ value, onChange, label, placeholder, rows = 4, className, ...props }) => {
  return (
    <div className={cn("relative", className)}>
      {label && (
        <label className="block text-sm font-medium mb-1.5">{label}</label>
      )}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={cn(
          "w-full px-4 py-3 rounded-xl border border-border bg-background resize-none",
          "text-base touch-manipulation",
          "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary",
          "placeholder:text-muted-foreground"
        )}
        {...props}
      />
    </div>
  );
};

export default {
  MobileCard,
  MobileListItem,
  FloatingActionButton,
  StickyBottomBar,
  MobileSectionHeader,
  MobileEmptyState,
  PullToRefreshIndicator,
  QuickActionButton,
  SwipeableCard,
  MobileDatePicker,
  MobileSelect,
  MobileInput,
  MobileTextarea
};
